import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import {
  BrandList,
  BrandForm,
  BrandDetail,
  ScrapeBrandForm,
} from "@/features/branding/components";
import {
  brandingService,
} from "@/features/branding/services/branding.service";
import type {
  Brand,
  CreateBrandParams,
  UpdateBrandParams,
  ScrapeBrandParams,
} from "@/features/branding/interfaces";

type ViewMode = "list" | "create" | "edit" | "scrape" | "detail";

export const Branding: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [brandToDelete, setBrandToDelete] = useState<{ id: number; title: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch all brands
  const {
    data: brands = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["brands"],
    queryFn: () => brandingService.getAllBrands(),
  });

  // Create brand mutation
  const createMutation = useMutation({
    mutationFn: (params: CreateBrandParams) =>
      brandingService.createBrand(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand created successfully");
      setViewMode("list");
    },
    onError: (error: any) => {
      toast.error(error || "Failed to create brand");
    },
  });

  // Update brand mutation
  const updateMutation = useMutation({
    mutationFn: (params: UpdateBrandParams) =>
      brandingService.updateBrand(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand updated successfully");
      setViewMode("list");
      setSelectedBrand(null);
    },
    onError: (error: any) => {
      toast.error(error || "Failed to update brand");
    },
  });

  // Delete brand mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => brandingService.deleteBrand({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand deleted successfully");
      setViewMode("list");
      setSelectedBrand(null);
      setBrandToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error || "Failed to delete brand");
    },
  });

  // Scrape brand mutation
  const scrapeMutation = useMutation({
    mutationFn: (params: ScrapeBrandParams) =>
      brandingService.scrapeBrand(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand scraped successfully");
      setViewMode("list");
    },
    onError: (error: any) => {
      toast.error(error || "Failed to scrape brand");
    },
  });

  const handleCreateNew = () => {
    setSelectedBrand(null);
    setViewMode("create");
  };

  const handleEdit = (brand: Brand) => {
    setSelectedBrand(brand);
    setViewMode("edit");
  };

  const handleDeleteClick = (brandId: number) => {
    const brand = brands.find((b) => b.id === brandId) ?? selectedBrand ?? null;
    setBrandToDelete(
      brand ? { id: brand.id, title: brand.title } : { id: brandId, title: "This brand" }
    );
  };

  const handleDeleteConfirm = async () => {
    if (!brandToDelete) return;
    await deleteMutation.mutateAsync(brandToDelete.id);
  };

  const handleScrape = () => {
    setViewMode("scrape");
  };

  const handleView = (brand: Brand) => {
    setSelectedBrand(brand);
    setViewMode("detail");
  };

  const handleCancel = () => {
    setViewMode("list");
    setSelectedBrand(null);
  };

  const handleFormSubmit = async (
    data: CreateBrandParams | UpdateBrandParams
  ) => {
    if (selectedBrand) {
      await updateMutation.mutateAsync(data as UpdateBrandParams);
    } else {
      await createMutation.mutateAsync(data as CreateBrandParams);
    }
  };

  const handleScrapeSubmit = async (data: ScrapeBrandParams) => {
    await scrapeMutation.mutateAsync(data);
  };

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <p className="text-destructive">
            Failed to load brands. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {viewMode === "list" && (
        <BrandList
          brands={brands}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onCreateNew={handleCreateNew}
          onScrape={handleScrape}
          onView={handleView}
          isLoading={isLoading}
        />
      )}

      {viewMode === "detail" && selectedBrand && (
        <BrandDetail
          brand={selectedBrand}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onBack={handleCancel}
        />
      )}

      <Dialog
        open={brandToDelete !== null}
        onOpenChange={(open) => !open && setBrandToDelete(null)}
        title="Delete brand?"
        description={
          brandToDelete
            ? `This will permanently delete "${brandToDelete.title}". This action cannot be undone.`
            : "This action cannot be undone."
        }
        size="sm"
      >
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setBrandToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={handleDeleteConfirm}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Dialog>

      {(viewMode === "create" || viewMode === "edit") && (
        <div className="max-w-4xl mx-auto w-full">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold">
                {viewMode === "edit" ? "Edit Brand" : "Create Brand"}
              </h1>
              <Button variant="ghost" size="icon" onClick={handleCancel} className="shrink-0 self-end sm:self-auto">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-muted-foreground">
              {viewMode === "edit"
                ? "Update your brand information"
                : "Add a new brand with images, videos, and information"}
            </p>
          </div>

          <div className="bg-card border border-input rounded-lg p-4 sm:p-6">
            <BrandForm
              brand={selectedBrand || undefined}
              onSubmit={handleFormSubmit}
              onCancel={handleCancel}
              isLoading={
                createMutation.isPending || updateMutation.isPending
              }
            />
          </div>
        </div>
      )}

      {viewMode === "scrape" && (
        <div className="max-w-2xl mx-auto w-full">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold">Scrape Brand</h1>
              <Button variant="ghost" size="icon" onClick={handleCancel} className="shrink-0 self-end sm:self-auto">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-muted-foreground">
              Automatically extract brand information from a website
            </p>
          </div>

          <div className="bg-card border border-input rounded-lg p-4 sm:p-6">
            <ScrapeBrandForm
              onSubmit={handleScrapeSubmit}
              onCancel={handleCancel}
              isLoading={scrapeMutation.isPending}
            />
          </div>
        </div>
      )}
    </div>
  );
};
